import {
  ArticleBullets,
  ArticleImage,
  ArticleLayout,
  ArticleParagraph,
  ArticleSection,
  QuickVerdict,
} from '../../../components/marketing/ArticleLayout';
import {
  ARTICLE_IMAGES,
  ARTICLE_SUMMARY_BY_SLUG,
} from '../../../lib/content/articles';

const article = ARTICLE_SUMMARY_BY_SLUG['a-balance-sheet-built-for-real-life'];

export default function BalanceSheetBuiltForRealLifePage() {
  return (
    <ArticleLayout
      article={article}
      relatedSlugs={['connect-everything-even-the-weird-stuff', 'budgeting-from-take-home-pay-not-gross-fantasy']}
    >
      <QuickVerdict
        title="Quick take"
        items={[
          'A household balance sheet is only useful if it captures the assets and liabilities that really shape decision-making.',
          'Per Diem treats mortgages, vehicles, manual accounts, and account-type differences as part of the planning model, not edge cases to ignore.',
          'The point is not a prettier net worth chart. The point is a more believable household picture.',
        ]}
      />

      <ArticleSection title="The average balance sheet is cleaner than the average life">
        <ArticleParagraph>
          Financial apps often present a polished version of the household balance sheet. It
          includes the easy accounts, leaves out the awkward ones, and makes the whole thing look
          more complete than it really is.
        </ArticleParagraph>
        <ArticleParagraph>
          That works for a quick snapshot, but it creates a real problem for planning. If the
          balance sheet only reflects the cleanest slice of the household, the household is then
          forced to keep the rest of its financial life in its head or in a spreadsheet.
        </ArticleParagraph>
      </ArticleSection>

      <ArticleImage
        source={ARTICLE_IMAGES.balanceSheet}
        alt="Per Diem balance sheet screen"
        caption="The useful version of a balance sheet is the one that helps a household make decisions, not just admire a total."
      />

      <ArticleSection title="What a real household balance sheet has to capture">
        <ArticleBullets
          items={[
            'Cash and investment accounts pulled in through sync providers.',
            'Liabilities such as mortgages and vehicle loans that shape monthly flexibility.',
            'Manual assets and accounts that still matter even if they are not directly connected.',
            'Account-type context such as HSAs, 529s, and tax-advantaged balances that change how the household uses them.',
            'A structure that can support planning, not just net worth display.',
          ]}
        />
      </ArticleSection>

      <ArticleSection title="Why liabilities deserve first-class treatment">
        <ArticleParagraph>
          A household’s flexibility is often constrained less by its assets than by the obligations
          attached to them. That is why liabilities need to be visible in the same system as the
          assets they interact with.
        </ArticleParagraph>
        <ArticleParagraph>
          A mortgage is not just a number subtracting from net worth. It affects housing costs,
          future cash flow, and the freedom the household actually feels. The same is true for
          vehicle loans and other recurring obligations.
        </ArticleParagraph>
      </ArticleSection>

      <ArticleImage
        source={ARTICLE_IMAGES.mobileAccounts}
        alt="Per Diem mobile accounts screen showing assets and liabilities"
        caption="The daily plan becomes more honest when the underlying household picture includes both the assets and the obligations that shape real choices."
        variant="phone"
      />

      <ArticleSection title="The balance sheet should feed the plan">
        <ArticleParagraph>
          A household does not maintain a balance sheet for its own sake. It does it because the
          balance sheet should improve the rest of the financial system: budgeting, day-to-day
          tradeoffs, and long-term FIRE planning.
        </ArticleParagraph>
        <ArticleParagraph>
          That is the product idea behind Per Diem. The balance sheet is not a separate “wealth
          tracking” feature. It is part of the same connected model that powers the daily freedom
          number and the long-term plan.
        </ArticleParagraph>
      </ArticleSection>

      <ArticleSection title="The goal is not perfection. It is coherence">
        <ArticleParagraph>
          No household model is ever perfectly complete. But a planning tool becomes much more
          useful when the balance sheet can hold the broad shape of the household honestly and keep
          that picture connected to everything else.
        </ArticleParagraph>
        <ArticleParagraph>
          That is what a balance sheet built for real life is supposed to do.
        </ArticleParagraph>
      </ArticleSection>
    </ArticleLayout>
  );
}
